import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'

import Tooltip from '@components/shared/Tooltip'
import Checkbox from '@components/shared/form/Checkbox'
import Dropdown from '@components/shared/form/Dropdown'
import Input from '@components/shared/form/Input'
import { useDialogStore } from '@stores/dialog.store'
import ApiRoutes from '@utils/api-routes'
import { dashifyCode } from '@utils/formatters/string.formatter'
import http from '@utils/http'

import eeBookExample from '../../../assets/images/eeBookExample.png'
import Dialog from '../Dialog'
import ErrorToast from '../ErrorToast'

import type { HTTPConfig } from '@utils/http'

// These values should match the enum defined in EeiExemption model file.
export enum ExemptionCodes {
  NO_CODE = 'no_code',
  ALREADY_USED = 'already_used',
  CANNOT_ACCESS = 'cannot_access',
  NOT_IN_SCHOOL = 'no_use_in_school',
  OTHER_REASON = 'other',
}

const CODE_LENGTH = 19 // 16 characters + dashes between them

interface Props {
  titleUuid: string
  afterActivate: () => void
}

const ActivateEEIDialog: React.FC<Props> = ({ titleUuid, afterActivate }) => {
  const { t } = useTranslation()
  const closeDialog = useDialogStore((s) => s.closeDialog)

  const REASON_OPTIONS = Object.freeze([
    { label: t('content.noCodeBook'), value: ExemptionCodes.NO_CODE },
    { label: t('content.codeAlreadyUsed'), value: ExemptionCodes.ALREADY_USED },
    { label: t('content.noBookAccess'), value: ExemptionCodes.CANNOT_ACCESS },
    { label: t('content.noEeSchool'), value: ExemptionCodes.NOT_IN_SCHOOL },
    { label: t('content.otherCode'), value: ExemptionCodes.OTHER_REASON },
  ])

  const [eeiCode, setEEICode] = useState('')
  const [codeMissing, setCodeMissing] = useState(false)
  const [parsedCode, setParsedCode] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [showHelp, setShowHelp] = useState(false)
  const [codeIsValid, setCodeIsValid] = useState(false)
  const [codeMissingReason, setCodeMissingReason] = useState<ExemptionCodes>()
  const [otherExplanation, setOtherExplanation] = useState('')

  // When the Help link is clicked, add an event listener to close the resulting tooltip on
  // next click.
  useEffect(() => {
    const body = document.querySelector('body')
    if (body) {
      if (showHelp) {
        body.addEventListener('click', toggleHelp)
      }
      return () => {
        body.removeEventListener('click', toggleHelp)
      }
    }
  }, [showHelp])

  const activate = async (e): Promise<boolean> => {
    e.preventDefault()
    e.stopPropagation()

    // If the user doesn't have a code and picked the option to explain why,
    // the explanation should be set.
    if (codeMissingReason === ExemptionCodes.OTHER_REASON && otherExplanation === '') {
      setErrorMsg(t(`errors.noCodeExplanation`))
      return false
    }

    // If they said they don't have a code but didn't set a reason why, error.
    if (codeMissing && !codeMissingReason) {
      setErrorMsg(t(`errors.noCodeReason`))
      return false
    }

    if (!codeMissing && !codeIsValid) {
      setErrorMsg(t(`errors.codeRequired`))
      return false
    }

    // Different endpoints and params are used based on whether we're activating a
    // code or an exemption.
    const postUrl = codeMissing
      ? ApiRoutes.apiActivateBookNoCodePath()
      : ApiRoutes.apiActivateLibraryCodePath()
    interface ActivateEEiParams extends HTTPConfig {
      title_uuid: string
      reason?: ExemptionCodes
      eei_code?: string
      explanation?: string
    }
    let params: ActivateEEiParams = { title_uuid: titleUuid, hideGlobalError: true }
    params = codeMissing
      ? { ...params, reason: codeMissingReason }
      : { ...params, eei_code: eeiCode }

    if (codeMissing && codeMissingReason === ExemptionCodes.OTHER_REASON)
      params.explanation = otherExplanation

    const [response, errors] = await http.post(postUrl, params)
    if (errors) {
      setErrorMsg(t(`errors.${errors[0].code}`))
    }
    if (response) {
      afterActivate()
    }

    return false // dont submit form
  }

  // regex to insert dashes and enforce only alphanumeric characters
  function handleInput(evt): void {
    const input = dashifyCode(evt.target.value, CODE_LENGTH)

    setCodeIsValid(input.length === CODE_LENGTH)
    setParsedCode(input)
    setEEICode(input)
  }

  function toggleHelp(e): void {
    e.preventDefault()
    setShowHelp(!showHelp)
  }

  // Fired when the checkbox for whether the user has an EEI code is toggled.
  function toggleCodeMissing(): void {
    setCodeMissing(!codeMissing)
  }

  function changeCodeMissingReason(e): void {
    setCodeMissingReason(e.target.value)
  }

  return (
    <Dialog
      name="activate_eei"
      title={t('action.enterActivationCode')}
      maxWidth={400}
      okBtn={{ action: activate, text: t('action.activate') }}
      cancelBtn={{ action: () => closeDialog('activate_eei') }}>
      {errorMsg && <ErrorToast error={errorMsg} onClearError={() => setErrorMsg('')} />}
      <form onSubmit={activate}>
        <p className="mb-3">{t('content.enterActivationCode')}</p>
        <Input
          name="eei-code"
          type="text"
          placeholder={t('action.enterCode')}
          value={parsedCode}
          onChange={handleInput}
          disabled={codeMissing}
        />

        <Tooltip
          arrow
          placement="bottom"
          content={<HelpDisplay />}
          offset={[65, 15]}
          trigger="click">
          <span className="cursor-pointer orange-link">{t('content.helpMeFindActivation')}</span>
        </Tooltip>
        <div className="my-4">
          <Checkbox
            name="codeMissing"
            label={t('content.noCode')}
            checked={codeMissing}
            onChecked={toggleCodeMissing}
          />
        </div>

        {codeMissing && (
          <>
            <Dropdown
              name="codeReason"
              value={codeMissingReason ?? ''}
              onChange={changeCodeMissingReason}>
              <option value="" disabled>
                {t('content.selectReasonCode')}
              </option>
              {REASON_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Dropdown>
            <OtherExplanationTextarea
              codeMissingReason={codeMissingReason}
              value={otherExplanation}
              onChange={setOtherExplanation}
            />
          </>
        )}
      </form>
    </Dialog>
  )
}

export default ActivateEEIDialog

interface OtherProps {
  codeMissingReason?: ExemptionCodes
  value: string
  onChange: (value: string) => void
}

// Textarea for the 'other' explanation for why the user has no code.
const OtherExplanationTextarea: React.FC<OtherProps> = ({
  codeMissingReason,
  value,
  onChange,
}: OtherProps) => {
  const { t } = useTranslation()
  if (codeMissingReason !== ExemptionCodes.OTHER_REASON) return null
  return (
    <div className="mt-2.5">
      <Input
        multiline
        value={value}
        name="otherReasonExplanation"
        autoComplete="off"
        rows={4}
        placeholder={t('content.noCodePlaceholder')}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

const HelpDisplay: React.FC = () => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-row py-1 pr-2">
      <div className="flex-shrink-0 basis-1/2 pr-1">
        <img src={eeBookExample} alt="EE Book" width={160} />
      </div>
      <div className="basis-1/2 flex flex-col justify-between">
        <p>{t('messages.findEeCode')}</p>
        <div>
          <p>Essential Elements Interactive Student Activation Code</p>
          <p className="font-semibold">ABCD-1234-ESF6-G7H8</p>
        </div>
      </div>
    </div>
  )
}
